/**
 * Covers on the screens (owner, 20 Oct 2026: "i want sunil to transfer
 * vehicles in factory today till the time amit is back").
 *
 * In the book of the person whose work a session covers, the rail and the
 * home page are that work's. Where the cover is the only way into that book,
 * the person is the covered job there — Amit's rail, Amit's home — and
 * nothing of their own; where they work in that book anyway, the covered
 * work is added to their own rail (AppShell). Rights follow the same rule in
 * `useAuth().has`, and the server answers the same way.
 */
import { BOOK_NAMES, type RoleKey, type SessionCover } from '@bright/shared';

import { useAuth, type AuthUser } from './auth';
import { BOOK_WHOLESALE, coverInUse, useActiveBook, type BookKey } from './book';

/**
 * The person as the covered job: its role and territory, standing in its
 * book — so a rail or home that asks "is this the maker's book?" of the
 * person answers for the covered book (review, 20 Oct 2026).
 */
export function actingAs(user: AuthUser, cover: Pick<SessionCover, 'role' | 'asmId' | 'organizationId' | 'book'>): AuthUser {
  const book = cover.book as BookKey | null;
  return {
    ...user,
    role: { ...user.role, key: cover.role.key as RoleKey, name: cover.role.name },
    asmId: cover.asmId,
    scopeParty: null,
    organizationId: cover.organizationId,
    organization: {
      id: cover.organizationId,
      name: BOOK_NAMES[cover.organizationId] ?? user.organization?.name ?? '',
      slug: user.organization?.slug ?? '',
      wholesale: book ? BOOK_WHOLESALE[book] : false,
    },
  };
}

/** The cover the book on screen is worked under, else null. */
export function useCoverInUse(): SessionCover | null {
  const { user } = useAuth();
  const book = useActiveBook();
  return coverInUse(user, book);
}

/** The session's person as the rail and the home page read them in the book on screen. */
export function useActingUser(): AuthUser | null {
  const { user } = useAuth();
  const cover = useCoverInUse();
  return user && cover?.crossBook ? actingAs(user, cover) : user;
}
